// @ts-strict-ignore
import React from 'react'
import { getValue } from '../../../utils/product-schema'
import { Icon, Button, ProviderImage, AuthorImage } from '../../../atoms'
import { EligibilityAddon, TableAddon } from './addons'
import { getMoreInformationContent } from '../utils'
import ejs from 'ejs/ejs'
import FDTemp from './fd-offer-temp'

const getAuthorEntity = (id, includes) => {
  if (!id) {
    return undefined
  }
  return includes.find((i) => i.sys.id === id)
}

function formatGetValue(value, data) {
  const val = getValue(value, data)

  if (typeof val === 'boolean') {
    return val ? 'Yes' : 'No'
  }

  return val
}

const createTopPicksError = (
  productName: string,
  productId: string,
  path: string,
  contentfulId: string,
  category: string,
) => {
  fetch('https://www.money.co.uk/opsgenie/error', {
    method: 'post',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      message: `top picks review missing for ${productName} on ${path} generate. Generate your review at https://product-reviewer.authed.rvu.cloud/?category=${category}&productId=${productId}`,
      tags: ['topPicksReviews'],
      alias: contentfulId,
    }),
  })
}

const HeadingContainer = ({
  isExclusive,
  isAd,
  isFDDeal,
  data,
  schema,
  brand,
}: {
  isExclusive: boolean
  isAd: boolean
  isFDDeal: boolean
  data: any
  schema: any
  brand: 'money' | 'uswitch'
}) => (
  <div
    className={`top-picks-card__heading-container ${
      isFDDeal ? 'fddeal-bg' : isExclusive ? 'exclusive-bg' : isAd ? 'ad-bg' : 'editor-bg'
    }`}
  >
    {isAd ? (
      <div className={'top-picks-card_ad'}>Ad</div>
    ) : (
      <Icon glyph={isExclusive || isFDDeal ? 'exclusive-deal' : 'editors-pick'} size={26} />
    )}
    <div
      className='top-picks-card__heading-container__heading'
      dangerouslySetInnerHTML={{
        __html:
          isExclusive || isFDDeal
            ? brand === 'money'
              ? 'money.co.uk <b>exclusive</b>'
              : 'Uswitch <b>exclusive</b>'
            : isAd
            ? '<b>Advertisement</b>'
            : '<b>Editor’s pick</b>',
      }}
    />
    <div className='top-picks-card__heading-container__sub-heading'>
      {getValue(schema.header.subtitle, data)}
    </div>
  </div>
)

const CardDetailsContainer = (
  variant: string,
  rawName: string,
  sys: { id: any },
  isExclusive: boolean,
  isAd: boolean,
  isFDDeal: boolean,
  isDesktopCard: boolean,
  data: any,
  schema: any,
  brand: 'money' | 'uswitch',
) => {
  const cardImage = getValue(schema.product.image, data)
  const ecUrl = data.subCategory
    ? `/credit-cards/eligibility-checker?product_id=${data._id}&productType=${data.subCategory}`
    : `/credit-cards/eligibility-checker?product_id=${data._id}`

  const ctaLink = getValue(schema.product.ctaLink, data)
  const clickOutUrl = ctaLink || ecUrl

  return (
    <div
      className={`top-picks-card__details ${
        isDesktopCard
          ? 'desktop-border'
          : isFDDeal
          ? 'fddeal-border-5'
          : isExclusive
          ? 'exclusive-border-5'
          : isAd
          ? 'ad-border-5'
          : 'editor-border-5'
      }`}
      data-top-picks
    >
      <div className='top-picks-card__details__img-container'>
        {cardImage && <ProviderImage src={cardImage} alt='Card' variant='small' />}
      </div>
      <div className='top-picks-card__details__name'>{getValue(schema.product.name, data)}</div>
      {getValue(schema.product.usp, data) && (
        <div className='top-picks-card__details__img-container__tag'>
          <div className='top-picks-card__details__img-container__tag__icon'>
            <Icon
              glyph={brand === 'money' ? 'credit-card-generic' : 'cash-payment-bag'}
              size={24}
              color={'#000'}
            />
          </div>

          <span className='top-picks-card__details__img-container__tag__text'>
            {getValue(schema.product.usp, data)}
          </span>
        </div>
      )}
      {schema.product.reward && (
        <div className='top-picks-card__details__img-container__tag'>
          <div className='top-picks-card__details__img-container__tag__icon'>
            <Icon glyph={schema.product.reward.icon} size={24} />
          </div>

          <span className='top-picks-card__details__img-container__tag__text'>
            {getValue(schema.product.reward.text, data)}
          </span>
        </div>
      )}
      {schema.product.productDetails.map((item: any, index: number) => {
        return (
          <div className='top-picks-card__details__table-rows' key={index}>
            <div className='top-picks-card__details__table-rows__name'>{item.label}</div>
            <div className='top-picks-card__details__table-rows__value'>
              {formatGetValue(item.value, data)}
            </div>
          </div>
        )
      })}
      {!isDesktopCard && schema.product.ctaText && (
        <div className='top-picks-card__details__button'>
          <Button
            href={clickOutUrl}
            variant='primary'
            size='md'
            openInNewTab
            /* eslint-disable camelcase */
            dataClickTracking={{
              component_category: 'platinum-component',
              component_id: sys?.id,
              component_name: 'TopPicksCard',
              contentful_name: rawName,
              contentful_variant: variant,
              additionalProps: { cardName: getValue(schema.product.name, data) },
            }}
          >
            {getValue(schema.product.ctaText, data)}
          </Button>
        </div>
      )}
      {schema.product.repExample && getValue(schema.product.repExample, data) && (
        <p className='top-picks-card__details__rep-example'>
          <b>Representative example:</b> {getValue(schema.product.repExample, data)}
        </p>
      )}
      {schema.product.warningInfo && getValue(schema.product.warningInfo, data) && (
        <p className='top-picks-card__details__warning-info'>
          {getValue(schema.product.warningInfo, data)}
        </p>
      )}
      {schema.product.addons && (
        <div className='top-picks-card__details__showDetailsCTA' data-top-picks-details-cta>
          <div
            className='top-picks-card__details__showDetailsCTA__text'
            data-top-picks-show-details-cta-text
          >
            Show Details
          </div>
          <div data-top-picks-chevron-down>
            <Icon
              glyph={'arrow-down'}
              size={24}
              color={brand === 'money' ? '#924A8B' : '#141414'}
            />
          </div>
          <div data-top-picks-chevron-up style={{ display: 'none' }}>
            <Icon glyph={'arrow-up'} size={24} color={brand === 'money' ? '#924A8B' : '#141414'} />
          </div>
        </div>
      )}
      {schema.product.addons &&
        schema.product.addons.map((addon, index) => {
          if (addon.view === 'eligibility') {
            return <EligibilityAddon addon={addon} data={data} key={index} />
          } else if (addon.view === 'table') {
            const addonContent = getMoreInformationContent(addon, data)

            return (
              <div
                className='top-picks-card__details__addons top-picks-card__details__addonsHide'
                data-top-picks-target='addon'
                key={index}
              >
                <div className='top-picks-card__details__addons__title'>{addon.title}</div>
                {addonContent && <TableAddon rows={addonContent} />}
              </div>
            )
          } else {
            return <></>
          }
        })}
    </div>
  )
}

const AuthorContainer = (data: any, schema: any, review: any, includes: any) => {
  const author = getAuthorEntity(review?.fields?.author?.sys?.id, includes)

  const authors = {
    'Salman Haqqi': {
      imgUrl:
        'https://img.uswitch.com/t014ej9w3ur1/7E2HJuSydm3blTEwN8dD3t/30a5fadd4f4c13a770109c2f6e4557ae/sal_money_headshot.jpg',
      pageUrl: '/authors/salman-haqqi',
      title: 'Personal finance expert',
    },
    'James Andrews': {
      imgUrl:
        'https://img.uswitch.com/t014ej9w3ur1/7xEl40fIdzlEL57R4tqgSX/0ceb1547426dee6e682c736cca2535d7/_L0I2441-Edit_Online_color_profile__2_.png',
      pageUrl: '/authors/james-andrews',
      title: 'Personal finance expert',
    },
  }
  const authorDetails =
    (author?.fields.name && author[author?.fields.name]) ||
    authors[getValue(schema.product.reviewName, data)] ||
    authors['Salman Haqqi']

  const authorImage =
    getAuthorEntity(author?.fields?.image?.sys?.id, includes)?.fields?.file?.url ||
    authorDetails.imgUrl
  const authorName = author?.fields.name || getValue(schema.product.reviewName, data)
  const authorPageUrl = author?.fields.url || authorDetails.pageUrl
  const authorTitle = author?.fields.role || authorDetails.title
  return (
    <div className='top-picks-card__bottom-section__about-author-container'>
      {authorImage && <AuthorImage src={authorImage} variant='small' />}

      <div>
        <div>
          <a
            className='top-picks-card__bottom-section__about-author-container__name'
            href={authorPageUrl}
          >
            {authorName}
          </a>
        </div>
        <div className='top-picks-card__bottom-section__about-author-container__designation'>
          {authorTitle}
        </div>
      </div>
    </div>
  )
}

export const TopPicksCard = (
  data: any,
  variant: string,
  rawName: string,
  sys: { id: string },
  schema: any,
  review: any,
  includes: any,
  brand: 'money' | 'uswitch',
  fullProductData: any,
  path: string,
) => {
  const isFDDeal = getValue(schema.header.typeOfPick, data) === 'uswitch exclusive'
  const isExclusive = getValue(schema.header.typeOfPick, data) === 'money exclusive' || isFDDeal
  const isAd = getValue(schema.header.typeOfPick, data) === 'ad'

  const miniReview = review?.fields?.minireview
  if (!miniReview && brand === 'money') {
    const productName = getValue(schema.product.name, data)
    createTopPicksError(productName, data._id, path, sys.id, data._source.definition.category)
  }

  return (
    <div
      className='top-picks-card'
      data-platinum-carousel-target='carouselCard'
      /* eslint-disable camelcase */
      data-scroll-tracking={JSON.stringify({
        component_category: 'platinum-component',
        component_id: sys?.id,
        component_name: 'top-picks',
        contentful_name: rawName,
        contentful_variant: variant,
        additionalProps: {
          title: data.subTitle,
          isAd,
          isExclusive,
          name: getValue(schema.product.name, data),
        },
      })}
    >
      <HeadingContainer
        isExclusive={isExclusive}
        isAd={isAd}
        isFDDeal={isFDDeal}
        data={data}
        schema={schema}
        brand={brand}
      />
      {CardDetailsContainer(
        variant,
        rawName,
        sys,
        isExclusive,
        isAd,
        isFDDeal,
        false,
        data,
        schema,
        brand,
      )}
      {isFDDeal ? (
        <div className={`top-picks-card__bottom-section fddeal-bg fddeal-border-2`}>
          <FDTemp />
        </div>
      ) : miniReview || getValue(schema.product.reviewText, fullProductData) ? (
        <div
          className={`top-picks-card__bottom-section ${
            isExclusive ? 'exclusive-border-2' : isAd ? 'ad-border-2' : 'editor-border-2'
          }`}
        >
          <p className='top-picks-card__bottom-section__description'>
            “
            {miniReview
              ? ejs.render(review.fields.minireview, fullProductData)
              : getValue(schema.product.reviewText, data)}
            ”
          </p>
          {AuthorContainer(data, schema, review, includes)}
        </div>
      ) : (
        <div />
      )}
    </div>
  )
}

export const TopPicksCardDesktop = (
  data: any,
  variant: string,
  rawName: string,
  sys: { id: string },
  schema: any,
  review: any,
  includes: any,
  brand: 'money' | 'uswitch',
  fullProductData: any,
  path: string,
) => {
  const isFDDeal = getValue(schema.header.typeOfPick, data) === 'uswitch exclusive'
  const isExclusive = getValue(schema.header.typeOfPick, data) === 'money exclusive' || isFDDeal
  const isAd = getValue(schema.header.typeOfPick, data) === 'ad'

  const ecUrl = data.subCategory
    ? `/credit-cards/eligibility-checker?product_id=${data._id}&productType=${data.subCategory}`
    : `/credit-cards/eligibility-checker?product_id=${data._id}`

  const ctaLink = getValue(schema.product.ctaLink, data) // ecUrl
  const clickOutUrl = ctaLink || ecUrl

  const miniReview = review?.fields?.minireview
  if (!miniReview && brand === 'money') {
    const productName = getValue(schema.product.name, data)
    createTopPicksError(productName, data._id, path, sys.id, data._source.definition.category)
  }

  return (
    <div className='top-picks-card-desktop'>
      <HeadingContainer
        isExclusive={isExclusive}
        isAd={isAd}
        isFDDeal={isFDDeal}
        data={data}
        schema={schema}
        brand={brand}
      />
      <div
        className={`top-picks-card-desktop__bottom-container ${
          isFDDeal
            ? 'fddeal-border-2'
            : isExclusive
            ? 'exclusive-border-2'
            : isAd
            ? 'ad-border-2'
            : 'editor-border-2'
        } ${isFDDeal ? 'top-picks-card-desktop__bottom-container__colorBackground' : ''}`}
      >
        <div
          className={
            getValue(schema.product.reviewText, data) || isAd || miniReview
              ? 'top-picks-card-desktop__bottom-container__grid'
              : 'top-picks-card-desktop__bottom-container__single-grid'
          }
        >
          {isFDDeal ? (
            <div className='top-picks-card-desktop__bottom-container__grid__sub-grid'>
              <FDTemp />
            </div>
          ) : miniReview || getValue(schema.product.reviewText, data) ? (
            <div className='top-picks-card-desktop__bottom-container__grid__sub-grid'>
              <div>
                <Icon
                  glyph={'quotemarks-stroke'}
                  size={45}
                  desktopSize={65}
                  color={brand === 'money' ? '#34454E' : '#141414'}
                />
                <p className='top-picks-card-desktop__bottom-container__desc'>
                  {miniReview
                    ? ejs.render(review.fields.minireview, fullProductData)
                    : getValue(schema.product.reviewText, data)}
                  ”
                </p>
              </div>
              {AuthorContainer(data, schema, review, includes)}
            </div>
          ) : (
            <div />
          )}
          <div>
            {CardDetailsContainer(
              variant,
              rawName,
              sys,
              isExclusive,
              isAd,
              isFDDeal,
              true,
              data,
              schema,
              brand,
            )}
          </div>
        </div>
        {schema.product.ctaText && (
          <div className='top-picks-card-desktop__bottom-container__button__container'>
            <Button
              href={clickOutUrl}
              variant='primary'
              size='lg'
              openInNewTab
              /* eslint-disable camelcase */
              dataClickTracking={{
                component_category: 'platinum-component',
                component_id: sys?.id,
                component_name: 'TopPicksCard',
                contentful_name: rawName,
                contentful_variant: variant,
                additionalProps: {
                  cardName: getValue(schema.product.name, data),
                },
              }}
            >
              {getValue(schema.product.ctaText, data)}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
